<template>
    <div>
        <div class="background--paper divider--container py-5 md:py-8">
            <div class="divider-white--top"></div>

            <div class="background--paper divider--container block md:hidden">
                <div class="divider-white--bottom"></div>
            </div>

            <div class="max-w-full lg:flex lg:container lg:mx-auto lg:max-w-4xl lg:pl-16 lg:pr-12">
                <div v-if="!hideCardSelection" class="container mx-auto text-center max-w-lg lg:max-w-md lg:pr-3">
                    <div class="md:border md:border-black bg-white">
                        <div class="md:px-10">
                            <h2 class="pt-3 pb-0" v-text="$t('PAYMENT_METHOD')"/>
                            <i v-if="loading" class="loader__loading-icon fa-spin"/>

                            <div v-if="isChargebeeCustomer">
                                <payment-methods v-if="!loading"></payment-methods>

                                <button v-if="!loading" class="btn btn--primary text-sm m-5" @click="showCardForm = !showCardForm"
                                    v-text="$t('PAYMENT_METHOD_ADD')">
                                </button>

                                <edit-card-details-form-chargebee
                                    class="mt-4"
                                    v-if="showCardForm"
                                    :autoClose="true"
                                    @closed="showCardForm = false; getPaymentSources()">
                                </edit-card-details-form-chargebee>
                            </div>
                        </div>

                        <div v-if="!isChargebeeCustomer" class="input__checkbox mx-auto py-5 px-8">
                            <input id="sameCard" type="checkbox" class="input__checkbox-item" v-model="sameCard">
                            <label v-if="cardEnding" for="sameCard" class="input__checkbox-label"
                                   v-html="$t('PAYMENT_USE_CARD', { 'cardEnding': $t(`${cardEnding}`) })">
                            </label>
                            <label v-if="user.payment_method === 'paypal'" for="sameCard" class="input__checkbox-label"
                                   v-html="$t('PAYMENT_USE_PAYPAL')">
                            </label>
                        </div>

                        <div class="md:px-10 pb-5" v-if="!sameCard && !isChargebeeCustomer">
                            <p v-if="errorMessage" class="mt-5 error" v-text="$t('PRIMER_CARD_A_PROBLEM')"></p>
                            <edit-card-details-form
                                v-if="showCardForm"
                                @same-card-toggle="sameCard = !sameCard"
                                :orderValue="orderValue * 100"
                                :submitButtonText="$t('PAYMENT_CONFIRM')"
                                class="mt-4"
                                :autoClose="true"
                                @closed="showCardForm = false"
                                @new-card-token="stripeToken">
                            </edit-card-details-form>
                        </div>
                    </div>
                </div>

                <div class="background--paper divider--container h-5 block md:hidden">
                    <div class="divider-white--top"></div>
                    <div class="divider-white--bottom"></div>
                </div>

                <payment-summary/>

                <div class="background--paper divider--container block md:hidden">
                    <div class="divider-white--top"></div>
                </div>
            </div>

            <div class="w-full p-2" v-if="errorHandler.getAllErrors()">
                <ul class="error list-disc text-center">
                    <li v-for="(errorMsg, key) in errorHandler.getAllErrors()" :key="key">
                        <span class="font-bold capitalize" v-text="key"/>:
                        <span class="font-normal" v-text="$t(errorMsg)"/>
                    </li>
                </ul>
            </div>

            <div class="divider-white--bottom"></div>
        </div>

        <div class="container mx-auto text-center max-w-lg py-10">
            <submit-button
                class="btn btn--primary"
                :disabled="isSubmitButtonDisabled"
                @click="submit"
                :processing="isFormProcessing">
                {{$t('PAYMENT_CONFIRM')}}
            </submit-button>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import PaymentSummary from '@/components/partials/account/gifting/PaymentSummary';
import EditCardDetailsForm from '@/components/forms/account/EditCardDetailsForm';
import EditCardDetailsFormChargebee from '@/components/forms/account/EditCardDetailsFormChargebee';
import PaymentMethods from '@/components/partials/account/gifting/PaymentMethods';

export default {
    name: 'Payment',
    components: { EditCardDetailsForm, EditCardDetailsFormChargebee, SubmitButton, PaymentSummary, PaymentMethods },
    data() {
        return {
            sameCard: true,
            cardToken: null,
            paymentMethod: null,
            showCardForm: false,
            errorMessage: false,
            errorHandler: new ErrorResponseHandler(),
            isFormProcessing: false,
            loading: false,
            stripeCardToken: null,
        };
    },
    computed: {
        ...mapGetters({
            deliveryDay: 'gifting/getDeliveryDateSelected',
            giftingAddress: 'address/getSelectedGiftingAddress',
            cardEnding: 'payment/getCardEnding',
            giftMessage: 'gifting/getGiftMessage',
            mobileNumber: 'gifting/getNotifierNumber',
            numberOfDeliveries: 'gifting/getNumberOfDeliveriesForSubscription',
            paidVase: 'gifting/getPaidVasePrice',
            isSubscription: 'gifting/isSubscription',
            frequency: 'gifting/getFrequencyForSubscription',
            includePaidVase: 'gifting/isPaidVaseAttached',
            user: 'account/getUser',
            selectedPaymentSource: 'payment/getSelectedPaymentSource',
            orderValue: 'gifting/getTotalPriceForDeliveries',
            additionalProducts: 'gifting/getAdditionalProducts',
            paymentGateway: 'payment/getStripeGateway',
            deliveryType: 'gifting/getDeliveryType',
            singleCoupon: 'gifting/getSingleGiftCoupon',
        }),

        billingAddress() {
            return this.$store.getters['payment/getBillingAddress'] || {};
        },

        isChargebeeCustomer() {
            return !this.user.is_payment_user;
        },

        isSubmitButtonDisabled() {
            const hasNotAddedNewCard = !this.sameCard && !this.cardToken;
            const hasNoChargebeePaymentSource = this.isChargebeeCustomer && !this.selectedPaymentSource;
            return hasNoChargebeePaymentSource;
        },
        hideCardSelection() {
            const isOneOffDelivery = this.deliveryType === 'one-off';
            const isFullyDiscounted = this.singleCoupon?.discount === 100 && this.singleCoupon?.discountType === 'percentage';
            return isOneOffDelivery && isFullyDiscounted;
        },
    },
    methods: {
        ...mapActions({
            fetchPaymentSources: 'payment/fetchPaymentSources',
            createGift: 'gifting/createGift',
        }),
        prepareParameters() {
            let payload = {
                delivery_day: this.$moment(this.deliveryDay).format('YYYY-MM-DD'),
                gifting_address_id: this.giftingAddress.id,
                include_message: true,
                is_multiple_deliveries: this.isSubscription,
                message_body: this.giftMessage.body,
                message_from: this.giftMessage.from,
                message_to: this.giftMessage.to,
                mobile_number: this.mobileNumber,
                number_of_deliveries: this.numberOfDeliveries,
                paid_vase: this.includePaidVase,
                payment_source_id: this.selectedPaymentSource,
            };
            if (!this.sameCard && this.stripeCardToken) {
                payload.card_token = this.stripeCardToken;
                payload.gateway = this.paymentGateway;
                payload.payment_method = 'card';
            }
            if (this.isSubscription) {
                payload.frequency = this.frequency;
            }
            if (this.additionalProducts) {
                payload.products = [];
                for (let key in this.additionalProducts) {
                    payload.products.push({
                        product_id: this.additionalProducts[key].productId,
                        quantity: this.additionalProducts[key].quantity,
                    });
                }
            }
            return payload;
        },
        submit() {
            this.isFormProcessing = true;
            this.errorMessage = false;
            this.processGiftViaApi();
        },
        processGiftViaApi() {
            this.createGift(this.prepareParameters())
                .then(response => this.handleSuccessfulResponse(response))
                .catch(error => {
                    if (error.response.data.errors) {
                        this.errorHandler.setResponse(error.response.data);
                    } else {
                        this.showCardForm = true;
                        this.sameCard = false;
                        this.errorMessage = true;
                    }
                    this.$root.$emit('payment-error', error);
                    this.isFormProcessing = false;
                });
        },
        handleSuccessfulResponse(response) {
            this.$store.commit('gifting/setGiftProcessed', true);
            this.$store.commit('gifting/setGiftReference', response.data.properties.reference);
            this.$store.commit('gifting/setGiftNoteUrl', response.data.properties.giftNoteUrl);
            this.$router.push({ name: 'account.gifting.thank-you' });
        },
        getPaymentSources() {
            this.loading = true;
            this.fetchPaymentSources(this.user.id).then(() => {
                this.loading = false;
            });
        },
        stripeToken(param) {
            this.stripeCardToken = param;
        },
    },
    created() {
        if (this.isChargebeeCustomer) {
            this.getPaymentSources();
        }
        this.$store.dispatch('cookies/mountStripe');
    },
    mounted() {
        // this.$root.$off('close-card-form');
        // this.$root.$on('close-card-form', (payload) => {
        //     this.showCardForm = false;
        //     this.cardToken = payload.token;
        //     this.paymentMethod = payload.payment_method;

        //     this.submit();
        // });
    },
    watch: {
        sameCard() {
            this.showCardForm = this.sameCard === false;
        },
    },
};
</script>
